import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";
import Button from "./Button";
import useBreakpoint from "../../hooks/useBreakpoint";
import { lockBodyScroll } from "../../helpers/scrollLock";
import "./Modal.scss";

// Fontaine M31 — the ONE modal API (V2 `modal` / `modal-delete`). Three shapes, one component:
//   dialog  — centred, for irreversible confirmations (Delete, Publish, Send)
//   drawer  — right-side panel on laptop, for quick edits that keep the page in view
//   sheet   — bottom sheet on phones (drag handle, swipe down to close)
//   auto    — sheet on phones, dialog otherwise (drawer callers pass variant="drawer": sheet on phones)
// Focus trap, Escape, scroll lock (reference-counted helper), stacked modals ladder, focus returned.
// Replaces react-modal (39 files) and react-sliding-pane for new code.

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
let openCount = 0;

/**
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {ReactNode} [title]                 also the accessible name
 * @param {ReactNode} [description]
 * @param {"auto"|"dialog"|"drawer"|"sheet"} [variant="auto"]
 * @param {"sm"|"md"|"lg"} [size="md"]        dialog 360 / 480 / 640; drawer 400 / 520 / 720
 * @param {ReactNode} [footer]                actions row (use ButtonGroup)
 * @param {boolean} [dismissible=true]        Escape / scrim / × close it
 * @param {string} [closeLabel]               word shown beside the ×. Default off, because a
 *                                            dialog over a dimmed page is obviously dismissible.
 *                                            Pass it where the overlay does NOT dim the page and
 *                                            has no scrim to click — Orders' quotes panel opens
 *                                            BESIDE the list, and there a bare glyph in the screen
 *                                            corner did not read as a control at all.
 * @param {boolean} [danger]                  red title accent for destructive confirms
 * @param {boolean} [lockScroll=true]         freeze the page behind. Pass false for a drawer that
 *                                            opens BESIDE the page rather than over it (Orders'
 *                                            quotes panel): there the page is still the thing the
 *                                            user is reading from, and locking it left the list
 *                                            unscrollable for as long as the panel was open.
 */
export default function Modal({ open, onClose, title, description, variant = "auto", size = "md", footer, dismissible = true, closeLabel, danger = false, lockScroll = true, className = "", children, ...rest }) {
  const id = useId().replace(/:/g, "");
  const { isPhone } = useBreakpoint();
  const shape = variant === "auto" ? (isPhone ? "sheet" : "dialog") : variant === "drawer" && isPhone ? "sheet" : variant;
  const panelRef = useRef(null);
  const lastFocus = useRef(null);
  const [layer, setLayer] = useState(0);
  const [dragY, setDragY] = useState(0);
  const drag = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    lastFocus.current = document.activeElement;
    openCount += 1;
    setLayer(openCount);
    const release = lockScroll ? lockBodyScroll() : null;
    const t = setTimeout(() => {
      const first = panelRef.current?.querySelector(FOCUSABLE);
      (first || panelRef.current)?.focus?.();
    }, 0);
    const onKey = (e) => {
      if (e.key === "Escape" && dismissible) { e.stopPropagation(); onClose?.(); return; }
      if (e.key !== "Tab" || !panelRef.current) return;
      const nodes = [...panelRef.current.querySelectorAll(FOCUSABLE)];
      if (!nodes.length) { e.preventDefault(); return; }
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      release?.();
      openCount = Math.max(0, openCount - 1);
      lastFocus.current?.focus?.();
    };
  }, [open, dismissible, lockScroll]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || typeof document === "undefined") return null;

  // sheet: drag the handle / header down to close
  const onPointerDown = (e) => { if (shape !== "sheet") return; drag.current = { y: e.clientY }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onPointerMove = (e) => { if (!drag.current) return; setDragY(Math.max(0, e.clientY - drag.current.y)); };
  const onPointerUp = () => { if (!drag.current) return; const dy = dragY; drag.current = null; setDragY(0); if (dy > 80 && dismissible) onClose?.(); };

  const titleId = `ft-modal-${id}-title`;
  const descId = `ft-modal-${id}-desc`;
  return createPortal(
    <div
      className={`ft-modal ft-modal--${shape}${danger ? " is-danger" : ""} ${className}`.trim()}
      data-layer={Math.min(layer, 3)}
      onMouseDown={(e) => { if (e.target === e.currentTarget && dismissible) onClose?.(); }}
      {...rest}
    >
      <div
        ref={panelRef}
        role={shape === "dialog" ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : rest["aria-label"] || "Dialog"}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`ft-modal__panel ft-modal__panel--${size}`}
        // eslint-disable-next-line no-restricted-syntax -- TODO(M02): live drag offset while swiping a sheet closed
        style={dragY ? { transform: `translateY(${dragY}px)` } : undefined}
      >
        {shape === "sheet" ? <div className="ft-modal__handle" aria-hidden="true" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} /> : null}
        {title || dismissible ? (
          <div className="ft-modal__head" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
            {title ? <h2 className="ft-modal__title" id={titleId}>{title}</h2> : <span />}
            {dismissible ? (
              <button type="button" className={`ft-modal__close${closeLabel ? " ft-modal__close--labelled" : ""}`} aria-label="Close" onClick={onClose}>
                <Icon name="X" size="sm" />
                {/* aria-hidden: `aria-label` above is already the accessible name, and a visible
                    label inside a labelled button would otherwise be announced twice. */}
                {closeLabel ? <span className="ft-modal__close-label" aria-hidden="true">{closeLabel}</span> : null}
              </button>
            ) : null}
          </div>
        ) : null}
        {description ? <p className="ft-modal__desc" id={descId}>{description}</p> : null}
        <div className="ft-modal__body">{children}</div>
        {footer ? <div className="ft-modal__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

/**
 * ConfirmDialog — the irreversible-action pattern (Delete, Publish, Send). Red button for destructive.
 */
export function ConfirmDialog({ open, onClose, onConfirm, title, description, confirmLabel = "Confirm", cancelLabel = "Cancel", danger = false, loading = false, children }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="dialog"
      size="sm"
      title={title}
      description={description}
      danger={danger}
      dismissible={!loading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>{cancelLabel}</Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
